
import { isMac as isMac_1 } from "../Interface/Bridge.js";
import { lookup, chordLabel, Chord, Mods, KeyName, Names_equal, Names_minus, Names_backspace, Names_delete, Names_space, Names_tab, Names_enter, Names_escape, Names_arrowDown, Names_arrowUp, Names_arrowRight, Names_arrowLeft, KeyContext, table } from "../Common/KeyTypes.js";
import { comparePrimitives, defaultOf, equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { MenuCommand, rightPaneTabVisible_, runAfterRender_, RunData, Msg, Pane, WaveSimState, SimSubTab, RightTab } from "../Model/ModelType.js";
import { openSheetIsReadOnly, getWSModel } from "./ModelHelpers.js";
import { FSharpMap__get_Item, containsKey, ofList } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { toArray, cons, truncate, empty, tail, isEmpty, ofArray } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { tryParse } from "../fable_modules/fable-library-js.5.13.0/Int32.js";
import { toString, Record, Union, FSharpRef } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, string_type, union_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { changeZoom } from "./WaveSim/WaveSimNavigation.js";
import { DrawModelType_SymbolT_ThemeType, DrawModelType_SheetT_WireTypeMsg, DrawModelType_SheetT_Arrange, DrawModelType_BusWireT_Msg, DrawModelType_SymbolT_Msg, DrawModelType_SheetT_Msg, DrawModelType_SheetT_KeyboardMsg } from "../Model/DrawModelType.js";
import { min, max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { KeyPressInfo, Rotation } from "../Common/CommonTypes.js";
import { Optic_Set, Optic_Set_op_HatEquals_Z147477F8 } from "../Common/Optics.js";
import { moveCustomPortsPopup } from "../DrawBlock/SymbolPortHelpers.js";
import { viewInfoPopup } from "./UIPopups.js";
import { debugLevel } from "../Interface/JSHelpers.js";
import { jsToBool } from "../Shared/ElectronAPI.js";
import { setPhysicalModifierHeld, isPhysicalModifierHeld } from "../DrawBlock/SheetDisplay.js";

export const isMac = isMac_1;

const platformTable = table(isMac);

/**
 * Which context the model puts the user in, ignoring DOM focus (which is read separately, at key
 * time, because it changes without the model changing).
 * 
 * First match wins, so the order is the priority order.
 */
export function contextOfModel(m) {
    if ((m.CodeEditorState != null) && (m.PopupViewFunc != null)) {
        return KeyContext.CodeEditor;
    }
    else if ((m.ProjectBrowser != null) && (m.PopupViewFunc != null)) {
        return KeyContext.ProjectBrowser;
    }
    else if (((m.PopupViewFunc != null) ? true : (m.SpinnerPayload != null)) ? true : (m.PopupDialogData.Progress != null)) {
        return KeyContext.Popup;
    }
    else if (((equals(m.RightPaneTabVisible, RightTab.Simulation) && equals(m.SimSubTabVisible, SimSubTab.WaveSim)) && equals(getWSModel(m).State, WaveSimState.Success)) && equals(m.KeyFocusPane, Pane.RightPane)) {
        return KeyContext.WaveSim;
    }
    else if (m.Sheet.Action.tag === 11) {
        if (m.CurrentProj != null) {
            return KeyContext.SheetIdle;
        }
        else {
            return KeyContext.Global;
        }
    }
    else {
        return KeyContext.SheetBusy;
    }
}

let modelContext = KeyContext.Global;

export function setContextFromModel(m) {
    modelContext = contextOfModel(m);
}

function isTextEntryFocused() {
    const matchValue = document.activeElement;
    if (equals(matchValue, defaultOf())) {
        return false;
    }
    else {
        const matchValue_1 = matchValue.tagName;
        switch (matchValue_1) {
            case "INPUT":
            case "TEXTAREA":
            case "SELECT":
                return true;
            default:
                return false;
        }
    }
}

/**
 * The context a key press should be resolved in.
 */
export function currentContext() {
    if (isTextEntryFocused()) {
        return KeyContext.TextEntry;
    }
    else {
        return modelContext;
    }
}

const namedKeys = ofList(ofArray([["ArrowLeft", Names_arrowLeft], ["ArrowRight", Names_arrowRight], ["ArrowUp", Names_arrowUp], ["ArrowDown", Names_arrowDown], ["Escape", Names_escape], ["Enter", Names_enter], ["Tab", Names_tab], [" ", Names_space], ["Delete", Names_delete], ["Backspace", Names_backspace], ["-", Names_minus], ["=", Names_equal], ["+", Names_equal]]), {
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
});

const namedCodes = ofList(ofArray([["Minus", Names_minus], ["Equal", Names_equal], ["NumpadSubtract", Names_minus], ["NumpadAdd", Names_equal], ["Space", Names_space]]), {
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
});

function isAsciiLetter(c) {
    if ((c >= "a") && (c <= "z")) {
        return true;
    }
    else if (c >= "A") {
        return c <= "Z";
    }
    else {
        return false;
    }
}

function isDigit(c) {
    if (c >= "0") {
        return c <= "9";
    }
    else {
        return false;
    }
}

/**
 * Resolve a key event to a KeyName.
 * 
 * ev.key alone will not do: on macOS Option rewrites it, so Cmd+Option+A arrives as "a" with a
 * ring over it. ev.code alone will not do either, being physical and so wrong on any layout that
 * is not QWERTY - Ctrl+A on AZERTY is physically KeyQ. So use ev.key wherever it is meaningful
 * and fall back to the physical key only where it is not.
 */
export function keyNameOf(ev) {
    let k, k_1, k_2, k_3, c, c_1, c_2;
    const key = ev.key;
    const code = ev.code;
    if ((k = key, ((k !== defaultOf()) && (k.length === 1)) && isAsciiLetter(k[0]))) {
        return new KeyName(/* KLetter */ 0, [key[0].toLocaleUpperCase()]);
    }
    else if ((k_1 = key, ((k_1 !== defaultOf()) && (k_1.length === 1)) && isDigit(k_1[0]))) {
        return new KeyName(/* KDigit */ 1, [key[0]]);
    }
    else if ((k_2 = key, (k_2 !== defaultOf()) && containsKey(k_2, namedKeys))) {
        return new KeyName(/* KNamed */ 3, [FSharpMap__get_Item(namedKeys, key)]);
    }
    else if ((k_3 = key, (((k_3 !== defaultOf()) && (k_3.length > 1)) && (k_3[0] === "F")) && isDigit(k_3[1]))) {
        const k_7 = key;
        let matchValue;
        let outArg = 0;
        matchValue = [tryParse(k_7.slice(1, k_7.length), 511, false, 32, new FSharpRef(() => (outArg | 0), (v) => {
            outArg = (v | 0);
        })), outArg];
        if (matchValue[0]) {
            return new KeyName(/* KFn */ 2, [matchValue[1]]);
        }
        else {
            return new KeyName(/* KNamed */ 3, [k_7]);
        }
    }
    else if ((c = code, ((c !== defaultOf()) && c.startsWith("Key")) && (c.length === 4))) {
        return new KeyName(/* KLetter */ 0, [code[3]]);
    }
    else if ((c_1 = code, ((c_1 !== defaultOf()) && c_1.startsWith("Digit")) && (c_1.length === 6))) {
        return new KeyName(/* KDigit */ 1, [code[5]]);
    }
    else if ((c_2 = code, (c_2 !== defaultOf()) && containsKey(c_2, namedCodes))) {
        return new KeyName(/* KNamed */ 3, [FSharpMap__get_Item(namedCodes, code)]);
    }
    else if (code !== defaultOf()) {
        return new KeyName(/* KNamed */ 3, [code]);
    }
    else {
        return new KeyName(/* KNamed */ 3, [(key === defaultOf()) ? "" : key]);
    }
}

export function modsOf(ev) {
    return new Mods(isMac ? ev.metaKey : ev.ctrlKey, isMac ? ev.ctrlKey : ev.metaKey, ev.altKey, ev.shiftKey);
}

export function chordOf(ev) {
    return new Chord(modsOf(ev), keyNameOf(ev));
}

class ZoomKey extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["ZoomIn", "ZoomOut", "ZoomToFit"];
    }
    static ZoomIn = new ZoomKey(0, []);
    static ZoomOut = new ZoomKey(1, []);
    static ZoomToFit = new ZoomKey(2, []);
}

function ZoomKey_$reflection() {
    return union_type("KeyBindings.ZoomKey", [], ZoomKey, () => [[], [], []]);
}

function waveSimHasTheKeyboard(m) {
    if ((equals(m.RightPaneTabVisible, RightTab.Simulation) && equals(m.SimSubTabVisible, SimSubTab.WaveSim)) && equals(getWSModel(m).State, WaveSimState.Success)) {
        return equals(m.KeyFocusPane, Pane.RightPane);
    }
    else {
        return false;
    }
}

function zoom(dispatch, key) {
    dispatch(new Msg(/* ExecFuncInMessage */ 116, [(m, d) => {
        const matchValue = waveSimHasTheKeyboard(m);
        let matchResult;
        if (matchValue) {
            switch (key.tag) {
                case 1: {
                    matchResult = 1;
                    break;
                }
                case 2: {
                    matchResult = 2;
                    break;
                }
                default:
                    matchResult = 0;
            }
        }
        else {
            switch (key.tag) {
                case 0: {
                    matchResult = 3;
                    break;
                }
                case 1: {
                    matchResult = 4;
                    break;
                }
                default:
                    matchResult = 2;
            }
        }
        switch (matchResult) {
            case 0: {
                changeZoom(getWSModel(m), true, d);
                break;
            }
            case 1: {
                changeZoom(getWSModel(m), false, d);
                break;
            }
            case 2: {
                d(new Msg(/* Sheet */ 3, [new DrawModelType_SheetT_Msg(/* KeyPress */ 2, [DrawModelType_SheetT_KeyboardMsg.CtrlW])]));
                break;
            }
            case 3: {
                d(new Msg(/* Sheet */ 3, [new DrawModelType_SheetT_Msg(/* KeyPress */ 2, [DrawModelType_SheetT_KeyboardMsg.ZoomIn])]));
                break;
            }
            case 4: {
                d(new Msg(/* Sheet */ 3, [new DrawModelType_SheetT_Msg(/* KeyPress */ 2, [DrawModelType_SheetT_KeyboardMsg.ZoomOut])]));
                break;
            }
        }
    }, dispatch]));
}

function focusComponentNameBox(_arg, model) {
    const matchValue = document.getElementById("labelInputElement");
    if (equals(matchValue, defaultOf())) {
    }
    else {
        const el = matchValue;
        el.focus();
        el.select();
    }
    return model;
}

export function actionOf(id, dispatch) {
    const sheetDispatch = (sMsg) => {
        dispatch(new Msg(/* Sheet */ 3, [sMsg]));
    };
    const keyDispatch = (k) => {
        sheetDispatch(new DrawModelType_SheetT_Msg(/* KeyPress */ 2, [k]));
    };
    const busWireDispatch = (bMsg) => {
        sheetDispatch(new DrawModelType_SheetT_Msg(/* Wire */ 0, [bMsg]));
    };
    const setTheme = (theme) => {
        dispatch(new Msg(/* SetThemeUserData */ 114, [theme]));
        busWireDispatch(new DrawModelType_BusWireT_Msg(/* Symbol */ 0, [new DrawModelType_SymbolT_Msg(/* SetTheme */ 43, [theme])]));
    };
    const stepAppZoom = (delta) => {
        window.issieBridge.window.setZoomLevel(max(-9, min(9, (window.issieBridge.window.getZoomLevel()) + delta)));
    };
    const ifEditable = (act) => {
        dispatch(new Msg(/* ExecFuncInMessage */ 116, [(m, _arg) => {
            if (!openSheetIsReadOnly(m)) {
                act();
            }
        }, dispatch]));
    };
    switch (id.tag) {
        case 1: {
            ifEditable(() => {
                keyDispatch(DrawModelType_SheetT_KeyboardMsg.CtrlV);
            });
            break;
        }
        case 2: {
            keyDispatch(DrawModelType_SheetT_KeyboardMsg.CtrlA);
            break;
        }
        case 3: {
            ifEditable(() => {
                keyDispatch(DrawModelType_SheetT_KeyboardMsg.DEL);
            });
            break;
        }
        case 4: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* Rotate */ 34, [Rotation.Degree90]));
            });
            break;
        }
        case 5: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* Rotate */ 34, [Rotation.Degree270]));
            });
            break;
        }
        case 6: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* Flip */ 35, ["flipVertical"]));
            });
            break;
        }
        case 7: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* Flip */ 35, ["flipHorizontal"]));
            });
            break;
        }
        case 8: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* Arrangement */ 36, [DrawModelType_SheetT_Arrange.AlignSymbols]));
            });
            break;
        }
        case 9: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* Arrangement */ 36, [DrawModelType_SheetT_Arrange.DistributeSymbols]));
            });
            break;
        }
        case 10: {
            ifEditable(() => {
                sheetDispatch(DrawModelType_SheetT_Msg.RotateLabels);
            });
            break;
        }
        case 11: {
            dispatch(new Msg(/* UpdateModel */ 41, [(m_1) => {
                let value_1;
                const matchValue = m_1.Sheet.SelectedComponents;
                const matchValue_1 = openSheetIsReadOnly(m_1);
                let matchResult;
                if (!isEmpty(matchValue)) {
                    if (isEmpty(tail(matchValue))) {
                        if (matchValue_1) {
                            matchResult = 1;
                        }
                        else {
                            matchResult = 0;
                        }
                    }
                    else {
                        matchResult = 1;
                    }
                }
                else {
                    matchResult = 1;
                }
                switch (matchResult) {
                    case 0:
                        return ((value_1 = (new RunData(false, focusComponentNameBox)), Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, runAfterRender_)(value_1)))(Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, rightPaneTabVisible_)(RightTab.Properties)(m_1));
                    default:
                        return m_1;
                }
            }]));
            break;
        }
        case 12: {
            ifEditable(() => {
                keyDispatch(DrawModelType_SheetT_KeyboardMsg.CtrlZ);
            });
            break;
        }
        case 13: {
            ifEditable(() => {
                keyDispatch(DrawModelType_SheetT_KeyboardMsg.CtrlY);
            });
            break;
        }
        case 14: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* RedrawWires */ 39, [false]));
            });
            break;
        }
        case 15: {
            ifEditable(() => {
                sheetDispatch(new DrawModelType_SheetT_Msg(/* RedrawWires */ 39, [true]));
            });
            break;
        }
        case 16: {
            ifEditable(() => {
                sheetDispatch(DrawModelType_SheetT_Msg.SeparateManualRouting);
            });
            break;
        }
        case 17: {
            dispatch(new Msg(/* ShowStaticInfoPopup */ 50, [["How to move component ports", moveCustomPortsPopup(), dispatch]]));
            break;
        }
        case 18: {
            keyDispatch(DrawModelType_SheetT_KeyboardMsg.ESC);
            break;
        }
        case 19: {
            dispatch(Msg.ClosePopup);
            break;
        }
        case 20: {
            dispatch(new Msg(/* MoveProjectBrowserSelection */ 95, [-1]));
            break;
        }
        case 21: {
            dispatch(new Msg(/* MoveProjectBrowserSelection */ 95, [1]));
            break;
        }
        case 22: {
            dispatch(new Msg(/* OpenProjectBrowserSelection */ 97, [dispatch]));
            break;
        }
        case 23: {
            dispatch(Msg.GoToProjectBrowserParent);
            break;
        }
        case 24: {
            sheetDispatch(DrawModelType_SheetT_Msg.ResetSelection);
            break;
        }
        case 25: {
            const matchValue_3 = document.activeElement;
            if (equals(matchValue_3, defaultOf())) {
            }
            else {
                matchValue_3.blur();
            }
            break;
        }
        case 26: {
            zoom(dispatch, ZoomKey.ZoomIn);
            break;
        }
        case 27: {
            zoom(dispatch, ZoomKey.ZoomOut);
            break;
        }
        case 28: {
            zoom(dispatch, ZoomKey.ZoomToFit);
            break;
        }
        case 29: {
            stepAppZoom(0.5);
            break;
        }
        case 30: {
            stepAppZoom(-0.5);
            break;
        }
        case 31: {
            window.issieBridge.window.setZoomLevel(0);
            break;
        }
        case 32: {
            window.issieBridge.window.toggleFullScreen();
            break;
        }
        case 33: {
            sheetDispatch(DrawModelType_SheetT_Msg.ToggleGrid);
            break;
        }
        case 34: {
            busWireDispatch(DrawModelType_BusWireT_Msg.ToggleArrowDisplay);
            break;
        }
        case 35: {
            sheetDispatch(new DrawModelType_SheetT_Msg(/* WireType */ 41, [DrawModelType_SheetT_WireTypeMsg.Jump]));
            break;
        }
        case 36: {
            sheetDispatch(new DrawModelType_SheetT_Msg(/* WireType */ 41, [DrawModelType_SheetT_WireTypeMsg.Radiussed]));
            break;
        }
        case 37: {
            sheetDispatch(new DrawModelType_SheetT_Msg(/* WireType */ 41, [DrawModelType_SheetT_WireTypeMsg.Modern]));
            break;
        }
        case 38: {
            setTheme(DrawModelType_SymbolT_ThemeType.Colourful);
            break;
        }
        case 39: {
            setTheme(DrawModelType_SymbolT_ThemeType.Light);
            break;
        }
        case 40: {
            setTheme(DrawModelType_SymbolT_ThemeType.White);
            break;
        }
        case 41: {
            dispatch(Msg.ChangeBuildTabVisibility);
            break;
        }
        case 42: {
            dispatch(new Msg(/* MenuAction */ 100, [MenuCommand.MenuNewFile, dispatch]));
            break;
        }
        case 43: {
            dispatch(new Msg(/* MenuAction */ 100, [MenuCommand.MenuSaveFile, dispatch]));
            break;
        }
        case 44: {
            viewInfoPopup(dispatch);
            break;
        }
        case 45: {
            dispatch(new Msg(/* MenuAction */ 100, [MenuCommand.MenuExit, dispatch]));
            break;
        }
        case 46: {
            dispatch(new Msg(/* WaveSimKeyPress */ 1, ["ArrowLeft"]));
            break;
        }
        case 47: {
            dispatch(new Msg(/* WaveSimKeyPress */ 1, ["ArrowRight"]));
            break;
        }
        case 48: {
            window.issieBridge.window.edit("copy");
            break;
        }
        case 49: {
            window.issieBridge.window.edit("cut");
            break;
        }
        case 50: {
            window.issieBridge.window.edit("paste");
            break;
        }
        case 51: {
            window.issieBridge.window.edit("selectAll");
            break;
        }
        case 52: {
            window.issieBridge.window.edit("undo");
            break;
        }
        case 53: {
            window.issieBridge.window.edit("redo");
            break;
        }
        case 54: {
            break;
        }
        case 55: {
            break;
        }
        case 56: {
            window.issieBridge.ipc.toggleDevTools();
            break;
        }
        case 57:
        case 58:
        case 59:
        case 60: {
            break;
        }
        default:
            ifEditable(() => {
                keyDispatch(DrawModelType_SheetT_KeyboardMsg.CtrlC);
            });
    }
}

/**
 * One resolution, for the shadow log.
 */
export class Resolution extends Record {
    constructor(Chord, Context, Resolved) {
        super();
        this.Chord = Chord;
        this.Context = Context;
        this.Resolved = Resolved;
    }
}

export function Resolution_$reflection() {
    return record_type("KeyBindings.Resolution", [], Resolution, () => [["Chord", string_type], ["Context", string_type], ["Resolved", string_type]]);
}

let keyLog = empty();

const keyLogLength = 40;

function record(chord, ctx, resolved) {
    const entry = new Resolution(chordLabel(isMac, chord), toString(ctx), (resolved == null) ? "(unbound - falls through)" : toString(resolved));
    keyLog = cons(entry, truncate(keyLogLength - 1, keyLog));
}

/**
 * Expose the shadow log to the outside, next to window.issie. Debug builds only.
 */
export function publishKeyLog() {
    if (debugLevel() > 0) {
        window.issieKeys = (() => toArray(keyLog));
    }
}

let spaceHeld = false;

function isModifierKey(key) {
    if (((key === "Control") ? true : (key === "Meta")) ? true : (key === "Alt")) {
        return true;
    }
    else {
        return key === "Shift";
    }
}

/**
 * The one key handler in Issie.
 */
export function onKeyDown(dispatch, e) {
    let option_1, KeyString, AltKey, ControlKey, MetaKey, s_2;
    const ev = e;
    if (!jsToBool(ev.isComposing) && (ev.keyCode !== 229)) {
        if (!isModifierKey(ev.key)) {
            const ctx = currentContext();
            const chord = chordOf(ev);
            const resolved = lookup(platformTable, ctx, chord);
            if (debugLevel() > 0) {
                record(chord, ctx, (option_1 = resolved, (option_1 != null) ? option_1.Id : undefined));
            }
            if (resolved == null) {
                if (equals(ctx, KeyContext.CodeEditor)) {
                    dispatch(new Msg(/* AnyKeyPress */ 0, [(KeyString = ev.key, (AltKey = ev.altKey, (ControlKey = ev.ctrlKey, (MetaKey = ev.metaKey, new KeyPressInfo(ev.shiftKey, ControlKey, AltKey, MetaKey, KeyString)))))]));
                }
            }
            else if (resolved.DevOnly && (debugLevel() === 0)) {
                const s_3 = resolved;
            }
            else if ((s_2 = resolved, jsToBool(ev.repeat) && !s_2.AllowRepeat)) {
                const s_4 = resolved;
                if (s_4.PreventDefault) {
                    ev.preventDefault();
                }
            }
            else {
                const s_5 = resolved;
                if (s_5.PreventDefault) {
                    ev.preventDefault();
                }
                actionOf(s_5.Id, dispatch);
            }
        }
        if (((ev.key === "Control") ? true : (ev.key === "Meta")) && !isPhysicalModifierHeld()) {
            setPhysicalModifierHeld(true);
            dispatch(new Msg(/* Sheet */ 3, [DrawModelType_SheetT_Msg.PortMovementStart]));
        }
        if ((ev.key === " ") && !spaceHeld) {
            const matchValue = currentContext();
            switch (matchValue.tag) {
                case 6:
                case 5: {
                    spaceHeld = true;
                    dispatch(new Msg(/* Sheet */ 3, [DrawModelType_SheetT_Msg.PanModeStart]));
                    break;
                }
                default:
                    undefined;
            }
        }
    }
}

export function onKeyUp(dispatch, e) {
    const ev = e;
    if (((ev.key === "Control") ? true : (ev.key === "Meta")) && isPhysicalModifierHeld()) {
        setPhysicalModifierHeld(false);
        dispatch(new Msg(/* Sheet */ 3, [DrawModelType_SheetT_Msg.PortMovementEnd]));
    }
    if ((ev.key === " ") && spaceHeld) {
        spaceHeld = false;
        dispatch(new Msg(/* Sheet */ 3, [DrawModelType_SheetT_Msg.PanModeEnd]));
    }
}

/**
 * Ctrl released while the window is not focused produces no keyup at all.
 */
export function onWindowBlur(dispatch, _arg) {
    if (isPhysicalModifierHeld()) {
        setPhysicalModifierHeld(false);
        dispatch(new Msg(/* Sheet */ 3, [DrawModelType_SheetT_Msg.PortMovementEnd]));
    }
    if (spaceHeld) {
        spaceHeld = false;
        dispatch(new Msg(/* Sheet */ 3, [DrawModelType_SheetT_Msg.PanModeEnd]));
    }
}

